import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useStatuses } from '../../context/StatusContext';
import { useLoading } from '../../context/LoadingContext';
import { useRoutePath } from '../../utils/getRoutePath';
import Breadcrumb from '../common/Breadcrumb';
import { useStatusCategories } from '../../context/StatusCategoriesContext';
import { ColorPicker } from "primereact/colorpicker";

// Validation schema
const schema = yup.object().shape({
  name: yup.string().required('Name is required'),
  status_category_id: yup.string().required('Status category is required'),
  color: yup.string().required('Color is required'),
  active: yup.boolean()
});

const JobStatusForm = ({ cData }) => {
  const getRoutePath = useRoutePath();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { id: routeId } = useParams();
  const queryId = searchParams.get("id");
  const id = queryId ?? routeId;
  const { addStatus, updateStatus, getStatusById } = useStatuses();
  const { showLoading, hideLoading } = useLoading();
  const { getStatusCategories } = useStatusCategories();
   
  const hasFetched = useRef(false);
  const [getEditData, setGetEditData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [statusCategories, setStatusCategories] = useState([]);
  const ranStatusCategoriesRef = useRef(false);
  const [color, setColor] = useState("c25757");

  const getDefaultValues = useCallback((existingData = null) => {
    const data = existingData || {};
    
    const defaults = {
      name: data?.name || '',
      status_category_id: data?.status_category_id || '',
      color: data?.color || '#3B82F6',
      active: data?.active ?? true
    };
     
    return defaults;
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    watch,
    reset,
    trigger,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: getDefaultValues(getEditData),
    mode: 'onChange'
  });

  useEffect(() => {
    const fetchStatusCategories = async () => {
      const response = await getStatusCategories(1, { all: false, type: 2 });
      setStatusCategories(response?.data);
    }
    if(ranStatusCategoriesRef && !ranStatusCategoriesRef.current){
      fetchStatusCategories();
      ranStatusCategoriesRef.current = true
    }
  }, [getStatusCategories]);

  useEffect(() => {
    if(cData){
      reset(getDefaultValues(cData))
      setSearchParams({id:cData?.id})
    }
  }, [cData, getDefaultValues, reset, setSearchParams])

  // Reset form when not in edit mode
  useEffect(() => {
    if (!id) {
      reset(getDefaultValues());
    }
  }, [id, reset, getDefaultValues]);

  const fetchStatusData = useCallback(async () => {
    if (id && !hasFetched.current) {
      hasFetched.current = true;
      showLoading();
      try {
        const response = await getStatusById(id);
        if (response?.status === 200 && response?.data) {
          const status = response.data;
           
          setGetEditData(status);
          // Reset form with fetched data
          reset(getDefaultValues(status));
            
        } else {
          toast.error('Status not found');
          navigate(getRoutePath('/job-statuses'));
        }
      } catch (error) {
        console.error('Error fetching status data:', error);
        toast.error('Error fetching status data');
      } finally {
        hideLoading();
      }
    }
  }, [id, getStatusById, showLoading, hideLoading, reset, getDefaultValues, navigate, getRoutePath]);

  useEffect(() => {
    fetchStatusData();
  }, [fetchStatusData]);

  const handleCancel = useCallback(() => {
    reset();
    navigate(getRoutePath('/job-statuses'));
  }, [reset, navigate, getRoutePath]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    showLoading();
    
    try {
      let response;
      
      if (id) {
        // Update existing status
        response = await updateStatus(id, data);
      } else {
        // Create new status
        response = await addStatus(data);
      }
      
      if (response.status === 200 || response.status === 201) {
        toast.success(id ? 'Status updated successfully!' : 'Status created successfully!');
        
        if(cData){
          navigate(getRoutePath('/calendar'));
        } else {
          navigate(getRoutePath('/job-statuses'));
        }
      
      } else if (response.status === 422) {
        // Handle validation errors
        if (response.errors) {
          Object.keys(response.errors).forEach(field => {
            const errorMessage = response.errors[field][0];
            setError(field, { message: errorMessage });
            toast.error(`${field}: ${errorMessage}`);
          });
        }
      } else {
        toast.error('Error saving status');
      }
    } catch (error) {
      toast.error('Error saving status');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
      hideLoading();
    }
  };

  useEffect(() => {
    setValue('color', '#'+color);
    trigger('color');
  }, [color,setValue,trigger]);

   
  return (
    <div className="container mx-auto px-4 py-8">
      {cData ? (
        <></>
      ) : (
        <>
          <Breadcrumb />
          <div className="mx-auto">
            <h1 className="text-2xl font-bold text-gray-900 mb-6">
              {id ? "Edit Job Status" : "Create New Job Status"}
            </h1>
          </div>
        </>
      )}
       
      <div className="bg-white rounded-lg shadow">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-6" autoComplete="off">
          {/* Status Information */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Status Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name <span className='text-red-500'>*</span></label>
                <input
                  type="text"
                  {...register('name')}
                  className={`mt-1 block w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                  placeholder="Enter status name"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Status Category <span className='text-red-500'>*</span></label>
                <select
                  {...register('status_category_id')}
                  value={watch('status_category_id') || ''}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                    errors.status_category_id ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                >
                  <option value="">Select Status Category</option>
                  {statusCategories?.map((category) => (
                    <option key={category.id} value={category.id} >
                      {category.name}
                    </option>
                  ))}
                </select>
                 
                {errors.status_category_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.status_category_id.message}</p>
                )}
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">Background Color <span className='text-red-500'>*</span></label>
              
              {/* Color Preview */}
              <div className="mt-2 flex items-center space-x-3">
                <div
                  className="w-10 h-10 rounded border border-gray-300"
                  style={{ backgroundColor: watch('color') }}
                ></div>
                <input
                  type="text"
                  {...register('color')}
                  className={`flex-1 px-3 py-2 border ${errors.color ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                  placeholder="#3B82F6"
                  
                />
              </div>
              {errors.color && (
                <p className="mt-1 text-sm text-red-600">{errors.color.message}</p>
              )}

              {/* Color Grid */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Choose from predefined colors:
                </label>
                <div className="grid grid-cols-5 gap-2">
                  <ColorPicker 
                    value={watch('color') || ''} 
                    onChange={(e) => setColor(e.value)} 
                    inline 
                    mode="slider"
                  />
 
                </div>
              </div>
            </div>

            <div className="flex space-x-6 mt-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('active')}
                  className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">Active</label>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-6 flex justify-end space-x-4">

          {cData ? (
            <></>
          ) : (
            <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
          >
            Cancel
          </button>
          )}
           
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Saving...' : (id ? 'Update Status' : 'Create Status')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobStatusForm;
